import mongoose from "mongoose";

export interface EmailNotification {
  _id: mongoose.Schema.Types.ObjectId;
  /**
   * note: from AWS, field can be either "notificationType" or "eventType".  Need to map appropriately.
   * "Unsubscribe" is a special status from PS Portal, not AWS.
   */
  notificationType: "Bounce" | "Complaint" | "Delivery" | "Unsubscribe";
  timestamp: Date; // date this record is created (new Date(), set by default to current time)

  /**
   * for filtering bounce/complaint reason and showing tooltip
   * Explains why the message was complained about (e.g. spam)
   * For notificationType = Bounce, Set to .complaint.complaintFeedbackType
   * For notificationType = Complaint, set to `${.bounce.bounceType} (${.bounce.bounceSubType})`
   * For other notificationType, set to undefined
   * Don't worry about Bounce and Complaint having inconsistent field value casing (e.g. lower vs capital)
   * Show the Description in a tooltip in UI (if there is a matching Field Value, otherwise no tooltip)
   *
   * Reason (formatted)                     Description
   * Undetermined (Undetermined)            Unable to determine a specific bounce reason.
   * Permanent (General)                    Hard bounce.
   * Permanent (NoEmail)                    Permanent hard bounce, because the target email address does not exist.
   * Permanent (Suppressed)                 Suppressed because the recipient has a recent history of bouncing.
   * Permanent (OnAccountSuppressionList)   Recipient has been suppressed by HP IT within AWS.
   * Transient (General)                    Temporary bounce.  May succeed later.
   * Transient (MailboxFull)                Recipient mailbox was full.
   * Transient (MessageTooLarge)            Recipient rejected message due to size constraints.
   * Transient (ContentRejected)            Recipient rejected message due to message content.
   * Transient (AttachmentRejected)         Recipient rejected message due to attachment.
   * abuse                                  Recipient reported message as unsolicited or spam.
   * auth-failure                           Recipient could not verify the authenticity of the message.
   * fraud                                  Recipient reported message as fraud or phishing.
   * not-spam                               Recipient has reversed previous complaint.
   * virus                                  Recipient reported message contained malicious content (e.g. a virus).
   * other                                  Recipient has complained for other reason.
   * Unsubscribe                            Recipient has unsubscribed from PS Portal emails.
   */
  reason?: string;
  reasonSubType?: string;

  /**
   * specific email this applies to, from bouncedRecipients or complainedRecipients
   * note that you should create one db entry per recipient so recipients can be individually "allowed" if needed
   */
  forEmail: string;

  // set if record is "allowed" - e.g. email service will ignore this record when deciding whether to send mail
  allowedAt?: Date;
  allowedBy?: string; // email of user who allowed this record

  // for notificationType = Bounce; otherwise undefined
  bounce?: {
    bounceType: string;
    bounceSubType: string;
    bouncedRecipients: string[]; // note: convert from { emailAddress: string }[] to string[]
    timestamp: Date; // convert from ISO-date string via new Date(str)
    feedbackId: string;
  }

  // for notificationType = Complaint; otherwise undefined
  complaint?: {
    complainedRecipients: string[]; // note: convert from { emailAddress: string }[] to string[]
    timestamp: Date; // convert from ISO-date string via new Date(str)
    feedbackId: string;
    complaintFeedbackType?: string;
    arrivalDate?: string; // probably an ISO-date string but we'll leave it as-is
  };

  mail?: {
    timestamp: Date; // convert from ISO-date string via new Date(str)
    source: string;
    sourceArn: string;
    sourceIp: string;
    callerIdentity: string;
    sendingAccountId: string;
    messageId: string;
    destination: string[];
    headersTruncated: boolean;
    headers?: { name: string; value: string; }[];
    commonHeaders?: {
      from: string[];
      date: string; // human-readable, can't make into Date() safely
      to?: string[];
      cc?: string[];
      bcc?: string[];
      messageId: string;
      subject?: string;
      [key: string]: unknown;
    };
    tags?: Record<string, string[]>;
  };
}

export const EmailNotificationSchema = new mongoose.Schema<EmailNotification>({
  notificationType: String,
  timestamp: { type: Date, default: () => new Date() },

  reason: String,
  reasonSubType: String,
  forEmail: String,

  allowedAt: Date,
  allowedBy: String,

  bounce: {
    _id: false,
    bounceType: String,
    bounceSubType: String,
    bouncedRecipients: [String],
    timestamp: Date,
    feedbackId: String,
  },

  complaint: {
    _id: false,
    complainedRecipients: [String],
    timestamp: Date,
    feedbackId: String,
    complaintFeedbackType: String,
    arrivalDate: String,
  },

  mail: {
    _id: false,
    timestamp: Date,
    source: String,
    sourceArn: String,
    sourceIp: String,
    callerIdentity: String,
    sendingAccountId: String,
    messageId: String,
    destination: [String],
    headersTruncated: Boolean,
    headers: [{
      _id: false,
      name: String,
      value: String,
    }],
    commonHeaders: mongoose.Schema.Types.Mixed,
    tags: mongoose.Schema.Types.Mixed,
  },
});

export const searchProps: (keyof EmailNotification)[] = [
  "forEmail" as keyof EmailNotification,
  "timestamp" as keyof EmailNotification,
];

export const filterProps: (keyof EmailNotification)[] = [
  "notificationType",
  "reason",
];

export const emailNotificationUtils = {
  searchProps,
  filterProps,
}
